import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Table from "../ogComponent/table";
import Spinner from "../ogComponent/spinner";
import axios from "axios";
import RoleModal from "../ogComponent/roleModal";
import { Modal, Box } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNavigate, useSearchParams } from "react-router-dom";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxWidth: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  outline: "none",
  borderRadius: '10px',
};

const AddUser = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [header, setHeader] = useState([]);
  const [roleCount, setRoleCount] = useState(0);
  const [editingRole, setEditingRole] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const [roleName, setRoleName] = useState("");
  const [checked, setChecked] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [modalKey, setModalKey] = useState(0);
  const navigate = useNavigate();

  const permissions = useSelector((state) => state.emp.permissions);
  const pagePermissions = ["showrolesID", "addrolesID", "deleterolesID", "editrolesID"];

  const handleRowClick = (item) => {
    navigate(`/user/roledetails/${item.id}`);
  };

  useEffect(() => {
    if (permissions.includes("showrolesID")) {
      fetchRoles();
    } else {
      setLoading(false);
    }
  }, [permissions]);

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.get(
        `${process.env.REACT_APP_API}/api/common/roles/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data) {
        const transformedRoles = response.data.map((role, index) => ({
          "SI NO": index + 1,
          id: role.id,
          role_name: role.role_name,
          edit: permissions.includes("editrolesID") ? (
            <div className="w-100 h-75 d-flex justify-content-end">
              <button
                className="btn btn-primary btn-sm me-2"
                onClick={(event) => {
                  event.stopPropagation();
                  handleEditRole(role);
                }}
              >
                <i className="bi bi-pencil"></i>
              </button>
            </div>
          ) : null,
          delete: permissions.includes("deleterolesID") ? (
            <div className="w-100 h-75 d-flex justify-content-end">
              <button
                className="btn btn-danger btn-sm"
                onClick={() => handleOpenDeleteModal(role)}
              >
                <i className="bi bi-trash"></i>
              </button>
            </div>
          ) : null,
        }));

        setRoles(transformedRoles);
        setHeader([
          { name: "SI NO", label: "#", active: true },
          { name: "role_name", label: "ROLE NAME", active: true },
          { name: "edit", label: "", active: permissions.includes("editrolesID") },
          { name: "delete", label: "", active: permissions.includes("deleterolesID") },
        ]);
        setRoleCount(transformedRoles.length);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditRole = async (role) => {
    if (permissions.includes("editrolesID")) {
      setEditingRole(role);
      setOpenEditModal(true);
      setOpenDeleteModal(false);
      setRoleToDelete(null);
      setModalKey((prev) => prev + 1);

      // Reset modal state
      setRoleName("");
      setChecked([]);
      setSelectedRoles([]);

      // Fetch employees
      let localEmployees = [];
      try {
        const token = localStorage.getItem("accessToken");
        const response = await axios.get(
          `${process.env.REACT_APP_API}/api/account/users-list/`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        localEmployees = response.data.map((employee) => ({
          value: employee.id,
          label: employee.name || employee.email,
          branch_id: employee.branch_id,
        }));
        setEmployees(localEmployees);
      } catch (error) {
        console.error("Error fetching employees:", error);
        return;
      }

      // Fetch role details
      try {
        const token = localStorage.getItem("accessToken");
        const response = await axios.get(
          `${process.env.REACT_APP_API}/api/common/roles/${role.id}/`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const roleData = response.data;

        // Update modal state
        setRoleName(roleData.role_name || "");
        setChecked(roleData.menus?.map((menu) => menu.id.toString()) || []);
        const userIds = roleData.users?.map((user) => user.id) || [];
        const selectedUsers = localEmployees.filter((emp) =>
          userIds.includes(emp.value)
        );
        setSelectedRoles([...selectedUsers]);

        // Debug logging
        console.log("Role Data:", roleData);
        console.log("Employees:", localEmployees);
        console.log("Selected Users:", selectedUsers);

        // Open Bootstrap modal
        const modalElement = document.getElementById("staticBackdrop");
        if (modalElement) {
          const bootstrap = window.bootstrap;
          const modal = bootstrap.Modal.getInstance(modalElement) || new bootstrap.Modal(modalElement);
          modal.show();
        }
      } catch (error) {
        console.error("Error fetching role details:", error);
      }
    }
  };

  const handleOpenDeleteModal = (role) => {
    if (permissions.includes("deleterolesID")) {
      setRoleToDelete(role);
      setOpenDeleteModal(true);
      setOpenEditModal(false);
      setEditingRole(null);
    }
  };

  const handleDelete = async (id) => {
    if (!permissions.includes("deleterolesID")) return;

    try {
      const token = localStorage.getItem("accessToken");
      await axios.delete(
        `${process.env.REACT_APP_API}/api/common/roles/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchRoles();
      handleCloseDeleteModal();
    } catch (error) {
      console.error("Error deleting role:", error);
    }
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setRoleToDelete(null);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setSearchParams({ page: newPage });
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setEditingRole(null);
    setRoleName("");
    setChecked([]);
    setSelectedRoles([]);
    const modalElement = document.getElementById("staticBackdrop");
    if (modalElement) {
      const bootstrap = window.bootstrap;
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) modal.hide();
    }
  };

  const handleCreateRole = async () => {
    setEditingRole(null);
    setOpenEditModal(true);
    setOpenDeleteModal(false);
    setRoleToDelete(null);
    setRoleName("");
    setChecked([]);
    setSelectedRoles([]);
    setModalKey((prev) => prev + 1);

    // Fetch employees for create mode
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.get(
        `${process.env.REACT_APP_API}/api/account/users-list/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const localEmployees = response.data.map((employee) => ({
        value: employee.id,
        label: employee.name || employee.email,
        branch_id: employee.branch_id,
      }));
      setEmployees(localEmployees);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }

    // Open Bootstrap modal
    const modalElement = document.getElementById("staticBackdrop");
    if (modalElement) {
      const bootstrap = window.bootstrap;
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  };

  return (
    <>
      <style>
        {`
      @media (max-width: 500px) {
        .modalmainbox {
          min-width: 75% !important;
        }
      }
    `}
      </style>
      <div className="addUserContainer w-100">
        <div className="tableMain d-flex flex-column px-3 px-md-4 py-3 py-md-4 gap-3">
          <div>
            <h4 className="fw-light mb-0">Roles</h4>
            <p
              className="fw-bold align-self-center mb-0"
              style={{ color: "grey", fontSize: "clamp(10px, 1.1vw, 12px)" }}
            >
              Total {roleCount} role{roleCount !== 1 ? "s" : ""}
            </p>
          </div>

          {permissions.includes("addrolesID") && (
            <div className="adduserButtons w-100 d-flex gap-2 gap-md-4">
              <button
                className="btn btn-primary d-flex gap-3 align-items-center text-nowrap px-3 py-1"
                style={{
                  width: "min-content",
                  fontSize: "clamp(10px, 1.1vw, 13px)",
                  height: "35px",
                }}
                onClick={handleCreateRole}
              >
                <i className="bi bi-plus-lg"></i> Create Role
              </button>
            </div>
          )}

          {loading ? (
            <Spinner />
          ) : permissions.includes("showrolesID") ? (
            roleCount === 0 ? (
              <div
                className="d-flex justify-content-center align-items-end position-relative"
                style={{ height: "50vh" }}
              >
                <div
                  className="position-absolute top-0 start-0 w-100 h-100"
                  style={{
                    backgroundImage: "url(/assets/file.png)",
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    opacity: 0.3,
                  }}
                ></div>
                <p className="m-0 fw-light fs-5 position-relative">No roles available!</p>
              </div>
            ) : (
              <Table
                header={header}
                product={roles}
                onRowClick={handleRowClick}
                loading={loading}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            )
          ) : (
            <p>No permission to view roles.</p>
          )}
        </div>
        {permissions.includes("addrolesID") && (
          <RoleModal
            key={modalKey}
            onRoleCreated={fetchRoles}
            setOpenModal={setOpenEditModal}
            editingRole={editingRole}
            onRoleUpdated={() => {
              fetchRoles();
              handleCloseEditModal(); // Added to close the modal after update
            }}
            roleName={roleName}
            setRoleName={setRoleName}
            checked={checked}
            setChecked={setChecked}
            selectedRoles={selectedRoles}
            setSelectedRoles={setSelectedRoles}
            employees={employees}
          />
        )}

        <Modal
          open={openDeleteModal && permissions.includes("deleterolesID")}
          onClose={handleCloseDeleteModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-description-description"
          style={{ outline: "none" }}
        >
          <Box sx={{ ...modalStyle, outline: "none", padding: '25px' }} className="flex-column">
            <div className="w-100 d-flex align-items-center">
              <div className="flex-column">
                <div>
                  <WarningAmberIcon sx={{ color: '#FFA000', fontSize: '60', width: '100%' }} />
                </div>
                <Box
                  sx={{
                    fontWeight: '500',
                    fontSize: 'clamp(13px, 1.4vw, 17px)',
                    mb: '2',
                  }}
                >
                  Confirm Delete
                </Box>
                <Box
                  sx={{
                    color: '#555',
                    mb: 2,
                    fontSize: 'clamp(12px, 1.2vw, 14px)',
                    textWrap: 'wrap',
                    textAlign: 'center',
                    paddingX: '10px'
                  }}
                >
                  Are you sure you want to delete the role "{roleToDelete?.role_name}"?
                </Box>
              </div>
            </div>
            <div className="w-100 d-flex justify-content-between flex-row-reverse">
              <button
                className="btn btn-danger"
                onClick={() => handleDelete(roleToDelete?.id)}
                style={{ fontSize: 'clamp(10px, 1.2vw, 15px)' }}
              >
                Confirm
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleCloseDeleteModal}
                style={{ fontSize: ' clamp(10px, 1.2vw, 15px)' }}
              >
                Cancel
              </button>
            </div>
          </Box>
        </Modal>
      </div>
    </>
  );
};

export default AddUser;